<template>
    <div class="admin-activity-manager">
        <div class="page-header">
            <div class="header-left">
                <el-icon class="header-icon">
                    <Collection />
                </el-icon>
                <h2>活动管理</h2>
            </div>
            <el-button type="primary" @click="showAddDialog = true">
                <el-icon>
                    <Plus />
                </el-icon> 新建活动
            </el-button>
        </div>

        <el-card shadow="never" class="table-card">
            <el-table :data="activities" v-loading="loading" stripe style="width: 100%">
                <el-table-column label="封面" width="120">
                    <template #default="{ row }">
                        <el-image :src="row.coverImage || defaultCover" class="activity-cover" fit="cover" />
                    </template>
                </el-table-column>
                <el-table-column prop="title" label="活动名称" min-width="200" show-overflow-tooltip />
                <el-table-column prop="price" label="费用" width="100">
                    <template #default="{ row }">
                        <el-tag :type="row.price > 0 ? 'warning' : 'success'">
                            {{ row.price > 0 ? `¥${row.price}` : '免费' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="status" label="状态" width="100">
                    <template #default="{ row }">
                        <el-tag :type="row.status === 'published' ? 'success' : 'info'">
                            {{ row.status === 'published' ? '已发布' : '草稿' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="enrollCount" label="报名数" width="100" />
                <el-table-column prop="createdAt" label="创建时间" width="180">
                    <template #default="{ row }">
                        {{ formatDate(row.createdAt) }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="200" fixed="right">
                    <template #default="{ row }">
                        <el-button-group>
                            <el-button size="small" @click="handleEdit(row)">编辑</el-button>
                            <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
                        </el-button-group>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Create Activity Dialog -->
        <el-dialog v-model="showAddDialog" title="新建活动" width="500px" destroy-on-close>
            <el-form :model="newActivity" label-width="80px">
                <el-form-item label="名称" required>
                    <el-input v-model="newActivity.title" placeholder="输入活动名称" />
                </el-form-item>
                <el-form-item label="报名费">
                    <el-input-number v-model="newActivity.price" :min="0" :precision="2" style="width: 100%" />
                    <div class="form-tip">0表示免费，非0则跳转支付</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showAddDialog = false">取消</el-button>
                <el-button type="primary" @click="submitAdd" :loading="submitting">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Collection, Plus } from '@element-plus/icons-vue'
import api from '@/utils/api'
import dayjs from 'dayjs'

const router = useRouter()
const activities = ref([])
const loading = ref(false)
const showAddDialog = ref(false)
const submitting = ref(false)
const defaultCover = "https://via.placeholder.com/300x180?text=Activity"

const newActivity = ref({
    title: '',
    price: 0,
    status: 'draft'
})

const fetchActivities = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/activities')
        activities.value = res.data.activities || []
    } catch (e) {
        ElMessage.error('加载列表失败')
    } finally {
        loading.value = false
    }
}

const submitAdd = async () => {
    if (!newActivity.value.title.trim()) {
        return ElMessage.warning('名称必填')
    }
    submitting.value = true
    try {
        const res = await api.post('/api/admin/activities', newActivity.value)
        ElMessage.success('创建成功')
        showAddDialog.value = false
        router.push(`/admin/activities/${res.data.id}/edit`)
    } catch (e) {
        ElMessage.error('创建失败')
    } finally {
        submitting.value = false
    }
}

const handleEdit = (row) => {
    router.push(`/admin/activities/${row.id}/edit`)
}

const handleDelete = (row) => {
    ElMessageBox.confirm(`确定删除活动 "${row.title}" 吗？此操作不可恢复。`, '警告', {
        type: 'warning'
    }).then(async () => {
        try {
            await api.delete(`/api/admin/activities/${row.id}`)
            ElMessage.success('已删除')
            fetchActivities()
        } catch (e) {
            ElMessage.error('删除失败')
        }
    })
}

const formatDate = (ts) => {
    return dayjs.unix(ts).format('YYYY-MM-DD HH:mm')
}

onMounted(fetchActivities)
</script>

<style scoped>
.admin-activity-manager {
    max-width: 1200px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.header-icon {
    font-size: 24px;
    color: var(--el-color-primary);
}

.page-header h2 {
    margin: 0;
    font-size: 20px;
}

.activity-cover {
    width: 100px;
    height: 60px;
    border-radius: 4px;
    background: #f5f7fa;
}

.table-card {
    border-radius: 12px;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}
</style>
